import './Field.scss';

import { useDispatch } from "react-redux";
import { useSelector } from "react-redux";

import { useMemo } from "react";

import { useFieldValidation } from "../../hooks/useFieldValidation";

import { updateField } from "../../slices/formSlice";
import { resetFieldOptions } from '../../slices/fieldOptionsSlice';

import { IndicatorManager } from "../IndicatorManager/IndicatorManager";
import { InputEmail } from "../Inputs/Inputs";
import { InputPassword } from "../Inputs/Inputs";
import { InputText } from "../Inputs/Inputs";
import { InputNumber } from "../Inputs/Inputs";
import { InputDateTime } from "../Inputs/Inputs";
import { InputDate } from "../Inputs/Inputs";
import { TextArea } from "../Inputs/Inputs";
import { CreateFields } from "../Dropdowns/Dropdowns";
import { BooleanDropdown } from "../Dropdowns/Dropdowns";
import { Dropdown } from "../Dropdowns/Dropdowns";
import { MultiSelectDropdown } from "../Dropdowns/Dropdowns";
import { DoubleInput } from "../Dropdowns/Dropdowns";

const buildDependencyMap = (fields) => {
    const map = {};
    fields.forEach(field => {
        const parent = field.parameters?.filter_by_field;
        const child = field.name;
        if (parent) {
            if (!map[parent]) {
                map[parent] = [];
            }
            map[parent].push(child);
        }
    });
    return map;
};

export const Field = ({
                          key,
                          label,
                          type,
                          name,
                          formId,
                          propertyLabel,
                          validationRules,
                          fieldStructure
                      }) => {
    const {
        value,
        error,
        handleChange
    } = useFieldValidation(formId, name, label, validationRules, type);

    const options       = useSelector(
        (s) => s.fieldOptions.options[name] || [],
    );

    const dispatch = useDispatch();
    const rawFieldList = useSelector(state => state.surveyFields.categories);

    const allFields = useMemo(() => {
        return rawFieldList.flatMap(category => category.fields);
    }, [rawFieldList]);

    const dependencyMap = useMemo(() => buildDependencyMap(allFields), [allFields]);

    const url            =
        typeof fieldStructure?.options === 'string'
            ? fieldStructure.options
            : null;

    const token = useSelector((state) => state.auth.token);
    const formValues = useSelector(state => state.form.forms[formId]?.values || {});

    const isEmpty =
        value === null ||
        value === undefined ||
        (typeof value === "string" && value.trim() === "") ||
        (Array.isArray(value) && value.length === 0) ||
        (typeof value === "object" && !Array.isArray(value) && Object.keys(value).length === 0);

    const isRequired = !!validationRules?.required;

    const subLabels = useMemo(() => {

        const baseKey = fieldStructure?.key;
        if (typeof baseKey !== "string" || !baseKey) return null;

        const prefix = `${baseKey}.*.`;
        const subs = allFields.filter(f => typeof f.key === "string" && f.key.startsWith(prefix));

        const nameField = subs.find(f => f.key.endsWith(".name"));
        const posField  = subs.find(f => f.key.endsWith(".position"));

        return {
            nameLabel: nameField?.label || "ФИО",
            positionLabel: posField?.label || "Должность"
        };
    }, [fieldStructure, allFields]);


    const handleWrappedChange = (newValue) => {
        handleChange(newValue);

        const dependents = dependencyMap[name] || [];

        dependents.forEach((dependentFieldName) => {
            const currentValue = formValues[dependentFieldName];

            if (currentValue != null) {
                dispatch(updateField({
                    formId,
                    fieldName: dependentFieldName,
                    value: null,
                }));
            }

            dispatch(resetFieldOptions(dependentFieldName));
        });
    };

    return (
        <div className={"field"} key={key}>
            <div className={"field_container"}>
                <div className={"field_header"}>
                    <IndicatorManager isRequired={isRequired} isEmpty={isEmpty} error={error} />
                    <label className={"field_label"}>{label}</label>
                </div>
                <div className={"field_main"}>
                    {type === "text" && (
                        <InputText propertyLabel={propertyLabel} value={value} onChange={handleWrappedChange} />
                    )}
                    {type === "number" && (
                        <InputNumber propertyLabel={propertyLabel} value={value} onChange={handleWrappedChange} minNumber={"0"} />
                    )}
                    {type === "multiselect" && (
                        <MultiSelectDropdown propertyLabel={propertyLabel} options={options} onChange={handleWrappedChange} url={url} fieldName={name} token={token} parentFieldName={fieldStructure?.parameters?.filter_by_field} parentValue={formValues[fieldStructure?.parameters?.filter_by_field]}/>
                    )}
                    {type === "datetime" && (
                        <InputDateTime propertyLabel={propertyLabel} value={value} onChange={handleWrappedChange} />
                    )}
                    {type === "date" && (
                        <InputDate propertyLabel={propertyLabel} value={value} onChange={handleWrappedChange} />
                    )}
                    {type === "textarea" && (
                        <TextArea propertyLabel={propertyLabel} value={value} onChange={handleWrappedChange} />
                    )}
                    {type === "boolean" && (
                        <BooleanDropdown propertyLabel={propertyLabel} options={options} value={value} onChange={handleWrappedChange} />
                    )}
                    {type === "dropdown" && (
                        <Dropdown options={options} propertyLabel={propertyLabel} value={value} onChange={handleWrappedChange} url={url} fieldName={name} token={token} parentFieldName={fieldStructure?.parameters?.filter_by_field} parentValue={formValues[fieldStructure?.parameters?.filter_by_field]}/>
                    )}
                    {type === "email" && (
                        <InputEmail value={value} onChange={handleWrappedChange} />
                    )}
                    {type === "password" && (
                        <InputPassword value={value} onChange={handleWrappedChange} />
                    )}
                    {(type === "string_array" || type === "multi_value") &&  (
                        <CreateFields
                            value={value}
                            onChange={handleWrappedChange}
                            nameLabel={subLabels?.nameLabel || propertyLabel}
                            positionLabel={subLabels?.positionLabel || "Должность"}
                        />
                    )}
                    {type === "double_input" && (
                        <DoubleInput
                            value={value}
                            onChange={handleWrappedChange}
                            nameLabel={subLabels?.nameLabel || propertyLabel}
                            positionLabel={subLabels?.positionLabel || "Должность"}
                        />
                    )}
                </div>
            </div>
        </div>
    );
};